(() => {
  const model = window.PrepMeQuickShopping;
  const error = (code) => Object.assign(new Error(code), { code });
  const request = (url, body) =>
    fetchWithDeadline(
      url,
      {
        method: body ? "POST" : "GET",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          ...(body ? { "Content-Type": "application/json" } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      },
      async (response) => {
        const data = await response.json();
        if (!response.ok)
          throw error(
            [400, 422].includes(response.status) &&
              !(response.status === 422 && (body?.items?.length > 1 || body?.items?.some((item) => item.quantity > 1))) &&
              typeof data?.description === "string"
              ? "rejected"
              : "uncertain",
          );
        return data;
      },
    );

  function cartBusy() {
    return window.PrepMeCartMutations.busy();
  }

  async function savePendingNote() {
    const note = customElements.get("cart-note");
    if (note?.draft !== null && note?.draft !== undefined) {
      await note.save();
      if (note.status === "error") throw error("read");
    }
  }

  function lockCart() {
    return window.PrepMeCartMutations.lock();
  }

  class PmPdpBestsellers extends HTMLElement {
    connectedCallback() {
      if (!this.bound) {
        this.bound = true;
        this.addEventListener("change", (event) => {
          const card = event.target.closest("[data-addon-card]");
          if (card) this.update(card);
        });
        this.addEventListener("click", (event) => {
          const button = event.target.closest("[data-addon-add]");
          if (button) this.add(button.closest("[data-addon-card]"));
        });
      }
      if (this.loaded) return;
      this.observer?.disconnect();
      // Optional, below-the-fold UI must not compete with first-paint resources.
      if ("IntersectionObserver" in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.observer.disconnect();
              if (!this.isConnected) return;
              if (document.readyState === "complete") this.load();
              else {
                this.onWindowLoad = () => {
                  if (this.isConnected) this.load();
                };
                window.addEventListener("load", this.onWindowLoad, {
                  once: true,
                });
              }
            }
          },
          { rootMargin: "300px" },
        );
        // The adjacent node may be stylesheet_tag output with no layout box.
        // Observe the visible product-info owner while this module stays hidden.
        this.observer.observe(this.parentElement);
      } else this.load();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      if (this.onWindowLoad)
        window.removeEventListener("load", this.onWindowLoad);
    }

    async load() {
      if (this.loaded) return;
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      try {
        const sources = await Promise.allSettled(
          [this.dataset.sourceUrl, this.dataset.newestSourceUrl].map(
            async (url) => {
              if (!url) return [];
              const text = await fetchWithDeadline(
                url,
                { signal: controller.signal },
                async (response) => {
                  if (!response.ok) throw error("read");
                  return response.text();
                },
              );
              const template = new DOMParser()
                .parseFromString(text, "text/html")
                .querySelector("template[data-meal-bestsellers]");
              if (!template) throw error("read");
              return [
                ...template.content.querySelectorAll("[data-meal-product]"),
              ];
            },
          ),
        );
        if (!this.isConnected || this.controller !== controller) return;
        const rows = this.querySelector("[data-meal-rows]");
        const eligible = (candidate) =>
          candidate.dataset.mealProduct &&
          candidate.dataset.mealProduct !== this.dataset.productId;
        const candidates = [
          ...new Map(
            (sources[0].value || [])
              .filter(eligible)
              .map((candidate) => [candidate.dataset.mealProduct, candidate]),
          ).values(),
        ].slice(0, 2);
        const selected = new Set(
          candidates.map((candidate) => candidate.dataset.mealProduct),
        );
        const newest = (sources[1].value || []).find(
          (candidate) =>
            eligible(candidate) && !selected.has(candidate.dataset.mealProduct),
        );
        if (newest) candidates.push(newest);
        candidates.forEach((candidate) => {
          const card = candidate.querySelector("[data-addon-card]");
          if (!card) return;
          // Feed IDs repeat across PDP sections. Scope labels before insertion.
          card.querySelectorAll("select[id]").forEach((select, index) => {
            const label = card.querySelector(`label[for="${select.id}"]`);
            select.id = `MealAddon-${this.dataset.sectionId}-${candidate.dataset.mealProduct}-${index}`;
            if (label) label.htmlFor = select.id;
            // Keep the empty prompt concise; the full option label stays visible in the picker.
            const prompt = [...select.options].find((option) => !option.value);
            if (prompt && label) {
              const name = label.textContent.trim();
              prompt.textContent = /veli|size/i.test(name)
                ? this.dataset.portionLabel
                : name;
            }
          });
          rows.append(card);
          try {
            card.variants = JSON.parse(
              card.querySelector("[data-addon-variants]").textContent,
            );
            if (
              !Array.isArray(card.variants) ||
              !card.variants.length ||
              !card.variants.every(
                (variant) =>
                  Number.isSafeInteger(variant.id) &&
                  Array.isArray(variant.options) &&
                  typeof variant.money === "string",
              )
            )
              throw error("read");
            card.initialPrice =
              card.querySelector("[data-addon-price]").textContent;
            if (!this.prepareChoices(card)) return;
            card.querySelector("[data-addon-add]").hidden = false;
            card.querySelector(".pm-upsell__fallback").hidden = true;
            this.update(card);
          } catch {
            /* Keep the product link, not an invalid add button. */
          }
        });
        this.hidden = rows.children.length === 0;
        this.loaded = !this.hidden;
      } catch {
        if (this.isConnected && this.controller === controller)
          this.hidden = true;
      }
    }

    prepareChoices(card) {
      card
        .querySelector(".pm-upsell__img-link img")
        ?.setAttribute("sizes", "44px");
      const choices = card.querySelector(".pm-upsell__options");
      if (!choices) return true;
      if (typeof choices.showPopover !== "function") {
        choices.hidden = true;
        return false;
      }
      const add = card.querySelector("[data-addon-add]");
      const trigger = add.cloneNode(true);
      choices.id = `${card.querySelector("select").id}-choices`;
      choices.setAttribute("popover", "auto");
      choices.setAttribute("role", "dialog");
      card.querySelector("select").setAttribute("autofocus", "");
      trigger.removeAttribute("data-addon-add");
      trigger.setAttribute("data-addon-choose", "");
      trigger.setAttribute("popovertarget", choices.id);
      trigger.setAttribute("aria-haspopup", "dialog");
      trigger.hidden = false;
      add.before(trigger);
      const header = document.createElement("div");
      header.className = "pm-pdp-bestsellers__choice-heading";
      const title = document.createElement("strong");
      title.id = `${choices.id}-title`;
      title.textContent = card
        .querySelector(".pm-upsell__card-name")
        .textContent.trim();
      choices.setAttribute("aria-labelledby", title.id);
      const close = document.createElement("button");
      close.type = "button";
      close.textContent = "×";
      close.setAttribute("aria-label", this.dataset.close);
      close.setAttribute("popovertarget", choices.id);
      close.setAttribute("popovertargetaction", "hide");
      header.append(title, close);
      choices.prepend(header);
      const actions = document.createElement("div");
      actions.className = "pm-pdp-bestsellers__choice-actions";
      const price = card.querySelector("[data-addon-price]").cloneNode(true);
      price.removeAttribute("data-addon-price");
      price.setAttribute("data-addon-choice-price", "");
      actions.append(price, add);
      choices.append(actions);
      return true;
    }

    update(card) {
      if (!card.variants) return;
      const selects = [...card.querySelectorAll("[data-addon-option]")];
      const values = selects.map((select) => select.value);
      card.variant =
        card.dataset.default === "true" && card.variants.length === 1
          ? card.variants[0].available === true
            ? card.variants[0]
            : undefined
          : model.resolveVariant(card.variants, values);
      selects.forEach((select, index) =>
        [...select.options].forEach((option) => {
          option.disabled =
            Boolean(option.value) &&
            !card.variants.some(
              (variant) =>
                variant.available === true &&
                variant.options[index] === option.value &&
                values.every(
                  (value, position) =>
                    position === index ||
                    !value ||
                    value === variant.options[position],
                ),
            );
        }),
      );
      card.querySelector("[data-addon-price]").textContent =
        card.variant?.money || card.initialPrice;
      const choicePrice = card.querySelector("[data-addon-choice-price]");
      if (choicePrice)
        choicePrice.textContent = card.variant?.money || card.initialPrice;
      const trigger = card.querySelector("[data-addon-choose]");
      if (trigger)
        trigger.disabled =
          !card.variants.some((variant) => variant.available === true) ||
          Boolean(window.PrepMeCartMutation) ||
          this.uncertain === true;
      card.querySelector("[data-addon-add]").disabled =
        !card.variant ||
        Boolean(window.PrepMeCartMutation) ||
        this.uncertain === true;
    }

    status(state, focus = false) {
      const feedback = this.querySelector(".pm-upsell__feedback");
      feedback.dataset.state = state;
      feedback.textContent = this.dataset[state];
      this.querySelector(".pm-upsell__recover").hidden = state !== "uncertain";
      if (focus) feedback.focus();
    }

    async add(card) {
      if (!card.variant || cartBusy() || this.uncertain) return;
      const choices = card.querySelector(".pm-upsell__options");
      if (choices?.matches?.(":popover-open")) choices.hidePopover();
      const variantId = card.variant.id;
      const unlock = lockCart();
      if (!unlock) return;
      this.setAttribute("aria-busy", "true");
      this.status("loading");
      let confirmed = false;
      let writesStarted = false;
      try {
        await savePendingNote();
        const cart =
          document.querySelector("cart-drawer") ||
          document.querySelector("cart-notification");
        const sections = cart
          ?.getSectionsToRender()
          .map((section) => section.id);
        writesStarted = true;
        const data = await request(this.dataset.addUrl, {
          items: [{ id: variantId, quantity: 1 }],
          ...(sections
            ? { sections, sections_url: window.location.pathname }
            : {}),
        });
        const added = data?.items?.find(
          (item) =>
            Number(item.variant_id || item.id) === variantId &&
            Number.isSafeInteger(item.quantity) &&
            item.quantity > 0,
        );
        if (!added) throw error("uncertain");
        confirmed = true;
        if (!this.isConnected) throw error("uncertain");
        if (cart) {
          cart.setActiveElement(
            card.querySelector("[data-addon-choose]") ||
              card.querySelector("[data-addon-add]"),
          );
          cart.renderContents({ ...added, sections: data.sections });
        } else {
          window.location.assign(this.dataset.cartUrl);
        }
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "pm-pdp-bestsellers",
          variantId,
          productVariantId: variantId,
          cartData: data,
          renderedSections: sections || [],
        });
        this.status("success", true);
      } catch (failure) {
        this.uncertain = writesStarted && failure.code !== "rejected";
        this.status(this.uncertain ? "uncertain" : "error", true);
        if (confirmed)
          this.querySelector(".pm-upsell__feedback").textContent =
            this.dataset.success;
      } finally {
        this.removeAttribute("aria-busy");
        if (!this.uncertain) {
          unlock();
          this.querySelectorAll("[data-addon-card]").forEach((item) =>
            this.update(item),
          );
        }
      }
    }
  }

  class PmCartVariantEditor extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      try {
        this.line = JSON.parse(
          this.querySelector("[data-edit-line]").textContent,
        );
        this.line.properties = model.normalizeProperties(this.line.properties);
        if (
          !Array.isArray(this.line.options) ||
          !Array.isArray(this.line.variants) ||
          !this.line.key
        )
          return;
        this.selects = [...this.querySelectorAll("[data-edit-option]")];
        this.selects.forEach((select) => {
          select.value = this.line.options[Number(select.dataset.editOption)];
          select.disabled = false;
        });
        this.addEventListener("change", (event) => {
          event.stopPropagation();
          this.update();
        });
        this.querySelector("[data-edit-apply]").addEventListener("click", () =>
          this.apply(),
        );
        this.bound = true;
        this.update();
        this.hidden = false;
        const editable = this.selects.map(
          (select) => select.dataset.editOption,
        );
        this.closest(".cart-item__details")
          ?.querySelectorAll("[data-cart-option-index]")
          .forEach((option) => {
            if (editable.includes(option.dataset.cartOptionIndex))
              option.hidden = true;
          });
      } catch {
        this.hidden = true;
      }
    }

    update() {
      const values = [...this.line.options];
      const editable = this.selects.map((select) =>
        Number(select.dataset.editOption),
      );
      this.selects.forEach((select) => {
        values[Number(select.dataset.editOption)] = select.value;
      });
      this.variant = model.resolveVariant(this.line.variants, values);
      this.selects.forEach((select) =>
        [...select.options].forEach((option) => {
          const options = [...values];
          options[Number(select.dataset.editOption)] = option.value;
          option.disabled = !model.resolveVariant(
            this.line.variants.filter((variant) =>
              model.compatibleOptions(
                this.line.options,
                variant.options,
                editable,
              ),
            ),
            options,
          );
        }),
      );
      const changed = values.some(
        (value, index) => value !== this.line.options[index],
      );
      this.dataset.changed = String(changed);
      this.selects.forEach((select) => {
        select.dataset.changed = String(
          select.value !== this.line.options[Number(select.dataset.editOption)],
        );
      });
      this.querySelector(".pm-cart-edit__actions").hidden = !changed;
      this.querySelector("[data-edit-price]").textContent =
        this.variant?.money || "";
      this.querySelector("[data-edit-apply]").disabled =
        !changed || !this.variant || this.uncertain === true;
    }

    async pickerProperties(variant) {
      const original = this.line.variants.find(
        (item) => item.id === this.line.variant_id,
      );
      if (
        !original?.mealCount ||
        original.mealCount !== variant.mealCount ||
        Number(this.line.properties["_Obroci-count"]) !== variant.mealCount
      )
        throw error("incompatible");
      const size = variant.options.find((value) =>
        /^(standard(no)?|xl)$/i.test(value.trim()),
      );
      if (!size) throw error("incompatible");
      const handle =
        size.trim().toLowerCase() === "xl"
          ? "monthly-subscription-obroci-xl"
          : "monthly-subscription-obroci-standard";
      const products = [];
      const seen = new Set();
      for (let page = 1; page <= 10; page++) {
        const data = await request(
          `${this.dataset.collectionsUrl}/${handle}/products.json?limit=250&page=${page}`,
        );
        if (
          !Array.isArray(data.products) ||
          data.products.some(
            (product) =>
              !Number.isSafeInteger(product.id) ||
              seen.has(product.id) ||
              !Array.isArray(product.variants),
          )
        )
          throw error("incompatible");
        data.products.forEach((product) => {
          seen.add(product.id);
          product.variants = product.variants.map((item) => ({
            ...item,
            options:
              item.options ||
              [item.option1, item.option2, item.option3].filter(
                (value) => value != null,
              ),
          }));
          products.push(product);
        });
        if (data.products.length < 250)
          return model.remapMeals(this.line.properties, size, products);
      }
      throw error("incompatible");
    }

    async apply() {
      if (!this.variant || cartBusy() || this.uncertain) return;
      const variant = this.variant;
      const unlock = lockCart();
      if (!unlock) return;
      this.setAttribute("aria-busy", "true");
      const status = this.querySelector(".pm-cart-edit__status");
      status.textContent = this.dataset.loading;
      let writesStarted = false;
      try {
        await savePendingNote();
        const properties = this.line.picker
          ? await this.pickerProperties(variant)
          : this.line.properties || {};
        const api = {
          read: () => request(`${this.dataset.cartUrl}.js`),
          add: (item) => {
            writesStarted = true;
            return request(this.dataset.addUrl, { items: [item] });
          },
          change: (key, quantity) =>
            request(this.dataset.changeUrl, { id: key, quantity }),
        };
        await model.replaceLine(api, this.line, variant, properties);
        if (this.closest("cart-drawer")) {
          try {
            sessionStorage.setItem("pm-reopen-cart", "true");
          } catch {
            /* Cart state remains authoritative. */
          }
        }
        // Reload all Shopify totals, discounts, delivery eligibility and section scripts together.
        window.location.reload();
      } catch (failure) {
        this.uncertain =
          writesStarted &&
          failure.code !== "rejected" &&
          failure.code !== "stale";
        status.textContent =
          this.dataset[
            this.uncertain
              ? "uncertain"
              : ["stale", "incompatible"].includes(failure.code)
                ? failure.code
                : "error"
          ] || this.dataset.error;
        this.querySelector(".pm-cart-edit__recover").hidden =
          !this.uncertain && failure.code !== "stale";
        status.focus();
      } finally {
        this.removeAttribute("aria-busy");
        if (!this.uncertain) {
          unlock();
          this.update();
        }
      }
    }
  }

  if (!customElements.get("pm-pdp-bestsellers"))
    customElements.define("pm-pdp-bestsellers", PmPdpBestsellers);
  if (!customElements.get("pm-cart-variant-editor"))
    customElements.define("pm-cart-variant-editor", PmCartVariantEditor);
  try {
    if (sessionStorage.getItem("pm-reopen-cart") === "true") {
      sessionStorage.removeItem("pm-reopen-cart");
      document.querySelector("cart-drawer")?.open();
    }
  } catch {
    /* Storage is optional. */
  }
})();
